import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PropertyCard from '@/components/base/PropertyCard';
import FilterBar from './components/FilterBar';
import MapView from './components/MapView';
import { properties } from '@/mocks/properties';

interface Filters {
  search: string;
  type: string;
  propertyType: string;
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  city: string;
}

const defaultFilters: Filters = {
  search: '',
  type: '',
  propertyType: '',
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  city: '',
};

const LISTINGS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'MyAbode Property Listings',
  description: 'Browse premium property listings available for sale and rent on MyAbode.',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/listings`,
  numberOfItems: properties.length,
  itemListElement: properties.slice(0, 10).map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: p.title,
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/listings/${p.id}`,
  })),
};

const ListingsPage = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({
    ...defaultFilters,
    type: searchParams.get('type') || '',
    propertyType: searchParams.get('type_prop') || '',
    city: searchParams.get('location') || '',
    search: searchParams.get('location') || '',
  });
  const [sortBy, setSortBy] = useState('featured');
  const [savedIds, setSavedIds] = useState<string[]>(['1', '4', '7']);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');

  useEffect(() => {
    const id = 'schema-listings';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(LISTINGS_SCHEMA);
    document.title = 'Property Listings | Buy & Rent Homes — MyAbode';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Browse and filter premium property listings on MyAbode. Find homes to buy or rent by location, type, bedrooms, and price range.');
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      type: searchParams.get('type') || prev.type,
      propertyType: searchParams.get('type_prop') || prev.propertyType,
    }));
  }, [searchParams]);

  const filtered = useMemo(() => {
    let result = [...properties];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.location.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q)
      );
    }
    if (filters.type) result = result.filter(p => p.priceType === filters.type);
    if (filters.propertyType) result = result.filter(p => p.type === filters.propertyType);
    if (filters.city) result = result.filter(p => p.city.toLowerCase().includes(filters.city.toLowerCase()));
    if (filters.bedrooms) result = result.filter(p => p.bedrooms >= parseInt(filters.bedrooms));
    if (filters.minPrice) result = result.filter(p => p.price >= parseInt(filters.minPrice));
    if (filters.maxPrice) result = result.filter(p => p.price <= parseInt(filters.maxPrice));

    if (sortBy === 'price_asc') result.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price_desc') result.sort((a, b) => b.price - a.price);
    else if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    else result.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));

    return result;
  }, [filters, sortBy]);

  const handleSave = (id: string) => {
    setSavedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-[72px]">
        {/* Page Header */}
        <div className="bg-stone-900 py-12 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2">Property Listings</h1>
            <p className="text-stone-400 text-sm">Browse our curated selection of premium properties</p>
          </div>
        </div>

        <FilterBar
          filters={filters}
          onChange={setFilters}
          onClear={() => setFilters(defaultFilters)}
          total={filtered.length}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Sort + View Toggle Row */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <p className="text-stone-500 text-sm">
              Showing <strong className="text-stone-800">{filtered.length}</strong> properties
            </p>
            <div className="flex items-center gap-3">
              {/* Sort (only in grid mode) */}
              {viewMode === 'grid' && (
                <div className="flex items-center gap-2">
                  <span className="text-stone-500 text-xs">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="bg-white border border-stone-200 rounded-lg px-3 py-1.5 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="rating">Top Rated</option>
                  </select>
                </div>
              )}

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    viewMode === 'grid'
                      ? 'bg-white text-stone-900'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className="ri-grid-line text-sm"></i>
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    viewMode === 'map'
                      ? 'bg-white text-stone-900'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className="ri-map-pin-line text-sm"></i>
                  Map
                </button>
              </div>
            </div>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <>
              {filtered.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map(property => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      saved={savedIds.includes(property.id)}
                      onSave={handleSave}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-24">
                  <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
                    <i className="ri-search-line text-2xl text-stone-400"></i>
                  </div>
                  <h3 className="text-stone-700 font-semibold mb-2">No properties found</h3>
                  <p className="text-stone-400 text-sm">Try adjusting your filters to see more results.</p>
                </div>
              )}
            </>
          )}

          {/* Map View */}
          {viewMode === 'map' && (
            <MapView
              properties={filtered}
              savedIds={savedIds}
              onSave={handleSave}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ListingsPage;
